import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWidth } from '~/hooks/use-width';
import {
  GRID_BREAKPOINTS,
  CONTAINER_MAX_WIDTHS,
  DEVICES,
  VIEWPORT,
} from './constants';

interface ViewportContextType {
  viewport: Viewport;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

type Viewport = {
  device: keyof typeof VIEWPORT;
  max_width: number;
};

const ViewportContext = createContext<ViewportContextType | undefined>(
  undefined
);

export const useViewport = (): ViewportContextType => {
  const context = useContext(ViewportContext);
  if (!context) {
    throw new Error('useViewport must be used within a ViewportProvider');
  }
  return context;
};

interface ViewportProviderProps {
  children: React.ReactNode;
}

export const ViewportProvider: React.FC<ViewportProviderProps> = ({
  children,
}) => {
  const [viewport, setViewport] = useState<Viewport>({
    device: VIEWPORT.desktop,
    max_width: CONTAINER_MAX_WIDTHS[DEVICES.desktop],
  });
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const { width } = useWidth();

  useEffect(() => {
    let mobile = false;
    let tablet = false;
    let desktop = false;
    if (width < GRID_BREAKPOINTS[DEVICES.tablet]) {
      mobile = true;
      setViewport({
        device: VIEWPORT.mobile,
        max_width: CONTAINER_MAX_WIDTHS[DEVICES.mobile],
      });
    } else if (width < GRID_BREAKPOINTS[DEVICES.desktop]) {
      tablet = true;
      setViewport({
        device: VIEWPORT.tablet,
        max_width: CONTAINER_MAX_WIDTHS[DEVICES.tablet],
      });
    } else {
      desktop = true;
      setViewport({
        device: VIEWPORT.desktop,
        max_width: CONTAINER_MAX_WIDTHS[DEVICES.desktop],
      });
    }
    setIsMobile(mobile);
    setIsTablet(tablet);
    setIsDesktop(desktop);
  }, [width]);

  return (
    <ViewportContext.Provider
      value={{ viewport, isMobile, isTablet, isDesktop }}
    >
      {children}
    </ViewportContext.Provider>
  );
};
